import type { OptionResult } from '@modules/search/components/search-filter-section/RoccoBasedSearchSort';
import SearchSort from '@modules/search/components/search-filter-section/RoccoBasedSearchSort';
import { useSearchSortOptions } from '~/modules/search/hook/use-search-sort-options';
import type { CategoryFilterSectionProps } from './CategoryFilterSection';
import FilterBarFilters from '@modules/search/components/search-filter-section/FilterBarFilters';
import { Link } from '@remix-run/react';
import { useMemo } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { trackBreadcrumbClick } from '~/modules/tracking/util/trackBreadcrumbClick';

export const CategoryFilterSection = ({
    category,
    componentBefore,
    componentBarAddon,
    breadcrumbs,
    searchFilters,
}: CategoryFilterSectionProps) => {
    const optionResult = useSearchSortOptions({ category });
    const tracker = useTracker();
    const allBreadcrumbs = useMemo(
        () =>
            breadcrumbs?.length
                ? [{ href: '/', label: 'Home' }, ...breadcrumbs]
                : null,
        [breadcrumbs]
    );

    return (
        <div className="px-12.5 pb-6">
            {/* Top row: filter controls (left) and component addons (right) */}
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {/* If caller provided componentBefore, render it; otherwise render the FilterBarFilters (original behaviour) */}
                    {componentBefore ? (
                        componentBefore
                    ) : (
                        <FilterBarFilters
                            filters={searchFilters.filterBarFilters ?? []}
                            updateFilter={searchFilters.updateFilter}
                            componentBarAddon={componentBarAddon}
                            filterStates={searchFilters.filterStates}
                        />
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {componentBarAddon}
                </div>
            </div>
            <div
                className="my-3 h-px"
                style={{ backgroundColor: 'rgba(20,20,20,0.2)' }}
            />

            {/* Bottom row: breadcrumbs (left) and sort dropdown (right) */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {allBreadcrumbs && (
                        <div className="flex items-center gap-3">
                            {allBreadcrumbs.map((breadcrumb, index) => (
                                <span
                                    key={breadcrumb.href}
                                    className="inline-flex items-center gap-2"
                                >
                                    <Link
                                        to={breadcrumb.href}
                                        onClick={() =>
                                            trackBreadcrumbClick(
                                                tracker,
                                                breadcrumb.href
                                            )
                                        }
                                    >
                                        <span className="text-[1.25rem] font-medium leading-[130%] tracking-[0.4] text-[#141414] hover:text-gray-700">
                                            {breadcrumb.label}
                                        </span>
                                    </Link>
                                    {index !== allBreadcrumbs.length - 1 && (
                                        <span className="text-[1.25rem] font-medium leading-[130%] tracking-[0.4] text-[#141414]">
                                            /
                                        </span>
                                    )}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center">
                    {optionResult.items.length > 0 && (
                        <SearchSort
                            optionResult={optionResult as OptionResult}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
